// Per-child controls (docs/ORGANISER_PLAN.md §5; mockups o06, o07). Set by the parent as
// `child.settings.set` events; each group (learn, visibility, arcade, …) is replaced whole, last
// writer wins. A child with no settings gets their age band's preset.
import { z } from 'zod'
import type { AgeBand } from './family'
import { playsOf, type Person } from './events'
import { tripEventOrder, type TripEvent } from './tripProject'

/**
 * A Learn track (WI-10). The tracks a trip has come from its language kit (src/domain/learn
 * `tracksFor`): `script:<deck>` per script the kit lists (script:katakana, script:hiragana,
 * script:hangul), plus everyday phrases, numbers and, once a script is under way, real words.
 * `script` is the older value, stored before each script was its own track: it still means
 * "every script track", so saved controls keep working unchanged.
 */
export const LearnTrack = z.union([z.enum(['everyday', 'script', 'together', 'numbers']), z.templateLiteral(['script:', z.string().regex(/^[a-z0-9][a-z0-9-]*$/)])])
export type LearnTrack = z.infer<typeof LearnTrack>

export const Visibility = z.object({
  /** all: the whole plan · window: today + `days` · stage: each stage on arrival · mystery: stages wrapped until their reveal date. */
  mode: z.enum(['all', 'window', 'stage', 'mystery']),
  days: z.number().int().min(0).max(7).default(1),
  /** Mystery mode: stage id → reveal date (YYYY-MM-DD). A stage with none opens on its first day. */
  reveals: z.record(z.string(), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).default({}),
})
export type Visibility = z.infer<typeof Visibility>

export const ChildSettings = z.object({
  learn: z.object({
    tracks: z.array(LearnTrack),
    goal: z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20)]),
    difficulty: z.enum(['gentle', 'standard', 'stretch']),
  }),
  visibility: Visibility,
  arcade: z.object({ maxPlays: z.union([z.literal(2), z.literal(3), z.literal(5)]) }),
  /** Children can add want-to-go places; they reach the parent as suggestions. */
  canSuggest: z.boolean(),
  /** What a child may change in the plan: nothing, items they made, or any unlocked item. */
  canEdit: z.enum(['no', 'own', 'unlocked']),
})
export type ChildSettings = z.infer<typeof ChildSettings>

export const ChildSettingsSet = z.object({ childId: z.string().min(1), fields: ChildSettings.partial() })

const base: ChildSettings = {
  learn: { tracks: ['everyday', 'script', 'numbers'], goal: 10, difficulty: 'standard' },
  visibility: { mode: 'all', days: 1, reveals: {} },
  arcade: { maxPlays: 3 },
  canSuggest: true,
  canEdit: 'no',
}

/** Starting points by age band; the parent can change any of it. Teens get the most say. */
export const PRESETS: Record<AgeBand, ChildSettings> = {
  u8: { ...base, learn: { tracks: ['everyday'], goal: 5, difficulty: 'gentle' }, visibility: { mode: 'mystery', days: 1, reveals: {} }, arcade: { maxPlays: 2 } },
  '8-12': { ...base, learn: { tracks: ['everyday', 'script', 'numbers'], goal: 5, difficulty: 'standard' }, visibility: { mode: 'window', days: 1, reveals: {} } },
  '13+': { ...base, canEdit: 'own' },
}

export function presetFor(ageBand: AgeBand | null | undefined): ChildSettings {
  return PRESETS[ageBand ?? '13+']
}

/** A child's controls: their age band's preset with the parent's changes folded in (each group replaced whole, in server order). */
export function settingsFor(child: Pick<Person, 'id' | 'ageBand'>, events: readonly TripEvent[]): ChildSettings {
  const s: ChildSettings = structuredClone(presetFor(child.ageBand))
  const mine = events.filter((e) => e.type === 'child.settings.set' && (e.payload as { childId?: unknown } | null)?.childId === child.id)
  for (const e of [...mine].sort(tripEventOrder)) {
    const p = ChildSettingsSet.safeParse(e.payload)
    if (p.success) Object.assign(s, p.data.fields)
  }
  return s
}

/**
 * A grown-up who plays (WI-37) has no age band and nobody above them: the whole trip, any
 * unlocked plan, their own learn goal and the most plays a day. Only `learn` and `arcade` are
 * ever folded in for them; nothing hides the plan from a grown-up or limits what they change.
 */
export const ADULT_SETTINGS: ChildSettings = {
  learn: { tracks: ['everyday', 'script', 'numbers'], goal: 10, difficulty: 'standard' },
  visibility: { mode: 'all', days: 1, reveals: {} },
  arcade: { maxPlays: 5 },
  canSuggest: true,
  canEdit: 'unlocked',
}

/**
 * A player's settings: a child's controls (`settingsFor`), or a grown-up player's adult
 * defaults; null for someone who doesn't play.
 */
export function settingsForPlayer(p: Pick<Person, 'id' | 'kind' | 'ageBand' | 'plays'>, events: readonly TripEvent[]): ChildSettings | null {
  if (p.kind === 'child') return settingsFor(p, events)
  if (!playsOf(p)) return null
  const s: ChildSettings = structuredClone(ADULT_SETTINGS)
  const mine = events.filter((e) => e.type === 'child.settings.set' && (e.payload as { childId?: unknown } | null)?.childId === p.id)
  for (const e of [...mine].sort(tripEventOrder)) {
    const parsed = ChildSettingsSet.safeParse(e.payload)
    if (!parsed.success) continue
    const { learn, arcade } = parsed.data.fields
    if (learn) s.learn = learn
    if (arcade) s.arcade = arcade
  }
  return s
}
